import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatVNDShort } from '@/lib/format';
import type { ReportMonitor } from '@/lib/api';

const PRIMARY_BAR = 'hsl(221 83% 53%)';
const FOCAL_BAR = 'hsl(262 83% 58%)';

export interface ReportTopSkusChartProps {
  monitors: ReportMonitor[];
  topN?: number;
}

function stripPrefix(name: string): string {
  return name
    .replace(/^Màn hình cong\s+/i, 'Cong · ')
    .replace(/^Màn hình\s+/i, '');
}

export function ReportTopSkusChart({ monitors, topN = 10 }: ReportTopSkusChartProps) {
  const top = monitors
    .filter((m) => (m.sales_revenue ?? 0) > 0)
    .sort((a, b) => b.sales_revenue - a.sales_revenue)
    .slice(0, topN);

  if (top.length === 0) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/30 p-8 text-center text-sm text-muted-foreground">
        Không có SKU nào có doanh thu trong kỳ này.
      </div>
    );
  }

  const maxRevenue = top[0]!.sales_revenue;
  const totalRevenue = top.reduce((s, m) => s + m.sales_revenue, 0);

  return (
    <div className="overflow-hidden rounded-lg border bg-card divide-y print:rounded-md">
      {top.map((sku, i) => {
        const widthPct = (sku.sales_revenue / maxRevenue) * 100;
        const sharePct = (sku.sales_revenue / totalRevenue) * 100;
        const isFocal = Boolean(sku.is_focal);
        return (
          <div
            key={sku.id}
            className="grid grid-cols-[2.5rem_1fr_8rem] items-center gap-3 p-3 transition-colors hover:bg-muted/30 print:gap-2 print:p-2"
          >
            {/* Rank + focal star */}
            <div className="flex shrink-0 flex-col items-center gap-0.5">
              <span
                className={cn(
                  'text-base font-bold tabular-nums leading-none',
                  i === 0 ? 'text-primary' : 'text-muted-foreground',
                )}
              >
                #{i + 1}
              </span>
              {isFocal && <Star className="h-3 w-3 fill-cat-focal text-cat-focal" />}
            </div>

            {/* Name + bar */}
            <div className="min-w-0 space-y-1">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{sku.id}</span>
                <span className="text-[10px] text-muted-foreground tabular-nums">{sharePct.toFixed(1)}% top</span>
              </div>
              <div
                className="line-clamp-2 text-[13px] font-medium leading-tight"
                title={sku.name}
              >
                {stripPrefix(sku.name)}
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${widthPct}%`,
                    background: isFocal ? FOCAL_BAR : PRIMARY_BAR,
                  }}
                />
              </div>
            </div>

            {/* Values */}
            <div className="shrink-0 text-right">
              <div className="text-sm font-semibold tabular-nums leading-none">
                {formatVNDShort(sku.sales_revenue)}
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground tabular-nums">
                {sku.sales_units.toLocaleString('vi-VN')} units
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
